import { ZodType, ZodTypeDef } from 'zod';

import { ResponseStatusInvalidFail } from '../errors';
import { mapOptionsToStrings } from '../utils';
import {
    RequestBuilderOptions,
    RequestMethod,
    RequestResult,
} from '../utils/types';
import { baseRequest, binaryRequest, fileRequest } from './baseRequest';

/**
 * RequestBuilder
 *
 * A helper class to help make building out requests easier using the Builder Pattern
 */
export class RequestBuilder<
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
> {
    baseUrl: string;

    body?: Record<string, unknown>;

    headers: Record<string, string>;

    params: Record<string, string>;

    queries: Record<string, string>;

    endpoint: string;

    method: RequestMethod;

    validator: ZodType<Output, Def, Input>;

    abortController?: AbortController;

    reporter?: (result: RequestResult<unknown>) => void;

    unAuthRetry:
        | null
        | ((builder: RequestBuilder<Output, Def, Input>) => Promise<unknown>) =
        null;

    constructor(
        method: RequestMethod,
        endpoint: string,
        baseUrl: string,
        {
            body,
            headers = {},
            params = {},
            queries = {},
            validator,
            abortController,
        }: RequestBuilderOptions<Output, Def, Input>,
    ) {
        this.endpoint = endpoint;
        this.method = method;
        this.baseUrl = baseUrl;
        this.body = body;
        this.headers = headers;
        this.params = params;
        this.queries = queries;
        this.queries = queries;
        this.abortController = abortController;

        this.validator = validator;
    }

    /**
     * withHeaders
     *
     * Merges given headers with the existing headers stored in the class
     *
     * @param headers Object containing header key/value pairs
     */
    withHeaders(headers: Record<string, string>) {
        this.headers = {
            ...this.headers,
            ...headers,
        };
        return this;
    }

    /**
     * withBody
     *
     * Merges given body with the existing body stored in the class
     *
     * @param body Object containing body key/value pairs
     */
    withBody(body: Record<string, unknown>) {
        this.body = {
            ...this.body,
            ...body,
        };
        this.headers = {
            ...this.headers,
            'Content-Type': 'application/json',
        };
        return this;
    }

    /**
     * withParams
     *
     * Merges given params with the existing params stored in the class
     *
     * @param params Object containing params key/value pairs
     */
    withParams(params: Record<string, string>) {
        this.params = {
            ...this.params,
            ...params,
        };
        return this;
    }

    /**
     * withQueries
     *
     * Merges given queries with the existing queries stored in the class
     *
     * @param queries Object containing queries key/value pairs
     */
    withQueries(queries?: Record<string, unknown>) {
        const mappedQueries = mapOptionsToStrings(queries);

        this.queries = {
            ...this.queries,
            ...mappedQueries,
        };
        return this;
    }

    /**
     * withAbortController
     *
     * Adds an AbortController to the request, this will replace any existing AbortController as only one can be sent
     * with the fetch request
     *
     * @param abortController - An instance of the AbortController
     */
    withAbortController(abortController: AbortController) {
        this.abortController = abortController;

        return this;
    }

    withUnAuthRetry(
        initFn: (
            builder: RequestBuilder<Output, Def, Input>,
        ) => Promise<unknown>,
    ) {
        this.unAuthRetry = initFn;

        return this;
    }

    withReporter(reporter: (result: RequestResult<unknown>) => void) {
        this.reporter = reporter;
        return this;
    }

    /**
     * send
     *
     * Finally sends the request build up using the RequestBuilder class
     */
    async send(): Promise<RequestResult<Output>> {
        const requestFn = () =>
            baseRequest(
                this.method,
                this.endpoint,
                this.baseUrl,
                {
                    params: this.params,
                    queries: this.queries,
                },
                this.headers,
                this.validator,
                this.body,
                this.abortController,
            );

        const result = await requestFn();

        if (this.reporter) {
            this.reporter(result);
        }

        if (this.unAuthRetry) {
            if (
                result.payload instanceof ResponseStatusInvalidFail &&
                result.payload.status === 401
            ) {
                await this.unAuthRetry(this);
                return requestFn();
            }

            return result;
        }

        return result;
    }

    /**
     * sendFile
     *
     * Sends a file as the final action of the RequestBuilder class
     */
    async sendFile(file: File): Promise<RequestResult<Output>> {
        const requestFn = () =>
            fileRequest(
                this.method,
                this.endpoint,
                this.baseUrl,
                {
                    params: this.params,
                    queries: this.queries,
                },
                this.headers,
                this.validator,
                file,
                this.abortController,
            );

        const result = await requestFn();

        if (this.reporter) {
            this.reporter(result);
        }

        if (this.unAuthRetry) {
            if (
                result.payload instanceof ResponseStatusInvalidFail &&
                result.payload.status === 401
            ) {
                await this.unAuthRetry(this);
                return requestFn();
            }

            return result;
        }

        return result;
    }

    /**
     * binary
     *
     * Send a request for a binary resource rather than a regular JSON request
     */
    async binary() {
        const requestFn = () =>
            binaryRequest(
                this.method,
                this.endpoint,
                this.baseUrl,
                {
                    params: this.params,
                    queries: this.queries,
                },
                this.headers,
                this.body,
                this.abortController,
            );

        const result = await requestFn();

        if (this.reporter) {
            this.reporter(result);
        }

        if (this.unAuthRetry) {
            if (
                result.payload instanceof ResponseStatusInvalidFail &&
                result.payload.status === 401
            ) {
                await this.unAuthRetry(this);
                return requestFn();
            }

            return result;
        }

        return result;
    }
}
